import { CSSProperties, FC, useCallback, useMemo, useState } from 'react';
import type { GridSelection } from '@glideapps/glide-data-grid';
import { captureException } from '@sentry/react';
import { Avatar, Button, Col, Divider, Dropdown, Row } from 'antd';
import { TbFileSpreadsheet, TbFileTypeCsv, TbTableExport } from 'react-icons/tb';

import { GoogleSheetsSVGIcon } from '@/assets';
import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import AddDataSourceModal from '@/shared/components/modals/ResourceCreationModals/AddDataSourceModal';
import { PlanTag } from '@/shared/components/tags';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { SmallTooltip } from '@/shared/components/tooltips';
import { getPlanHistoricalDataYears } from '@/shared/pricing';
import { useConnectedAccountStore, useUserStore } from '@/stores';
import { imgLogoStyling } from '@/theme';
import { ConnectedAccount, ConnectedAccountType } from '@/types';
import { formatFinancialLineItemLabel } from '@/utilities/helpers/formatFinancialLineItemLabel';
import { downloadSpreadsheet } from '@/utilities/spreadsheet';

interface GlideDataGridTableExportDropdownProps {
    buttonStyle?: CSSProperties;
    company?: any;
    data?: any[];
    displayRows?: Array<Record<string, unknown>>;
    schema: string[];
    segmentedValue: string;
    selection?: GridSelection;
}

const reorderAndRemove = (obj: Record<string, any>, order: string[]): Record<string, any> => {
    const newObj: Record<string, any> = {};

    for (const key of order) {
        if (key in obj) {
            newObj[key] = obj[key];
        }
    }

    return newObj;
};

const processArray = (dataArray: Record<string, any>[], order: string[]): Record<string, any>[] =>
    dataArray.map((obj) => reorderAndRemove(obj, order));

const GlideDataGridTableExportDropdown: FC<GlideDataGridTableExportDropdownProps> = ({
    buttonStyle,
    company,
    data,
    displayRows,
    schema,
    segmentedValue,
    selection,
}): JSX.Element => {
    const [exporting, setExporting] = useState(false);
    const [exportingFormat, setExportingFormat] = useState<'csv' | 'xlsx' | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [dataSourcesModalOpen, setDataSourcesModalOpen] = useState(false);

    const { token } = useAntDesignTheme();
    const { selectedUser: user } = useUserStore();
    const { hasMinimumPlan } = useEntitlements();
    const { connectedAccounts } = useConnectedAccountStore();
    const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);
    const companySymbol = company?.symbol ?? company?.tickerSymbol;

    const columns = useMemo(
        () => [
            {
                title: 'Line Items',
                id: 'lineItems',
            },
            {
                title: `${segmentedValue === 'annual' ? 'YoY' : 'QoQ'} Change`,
                id: 'change',
            },
            ...rows.map((item: any) => ({
                title: String(item?.periodLabel),
                id: `items${item?.periodLabel}`,
            })),
        ],
        [rows, segmentedValue],
    );

    const periods = useMemo(() => rows.map((obj: any) => obj?.periodLabel) ?? [], [rows]);
    const historicalDataYears = getPlanHistoricalDataYears(user?.plan);
    const allowedPeriodCount = useMemo(() => {
        if (historicalDataYears === 'unlimited') {
            return periods.length;
        }

        return Math.min(periods.length, segmentedValue === 'annual' ? historicalDataYears : historicalDataYears * 4);
    }, [historicalDataYears, periods.length, segmentedValue]);
    const lockedPeriodColumnIds = useMemo(() => {
        const lockedCount = Math.max(periods.length - allowedPeriodCount, 0);

        return new Set(periods.slice(0, lockedCount).map((period: any) => `items${period}`));
    }, [allowedPeriodCount, periods]);
    const processedDataArray = useMemo(() => processArray(rows, schema), [rows, schema]);
    const keys = useMemo(
        () => (processedDataArray.length ? Object.keys(processedDataArray[0]) : []),
        [processedDataArray],
    );
    const dataByPeriod = useMemo(() => Object.fromEntries(rows.map((item: any) => [item.periodLabel, item])), [rows]);
    const dummyData = useMemo(() => {
        if (displayRows) {
            return displayRows;
        }

        return keys.map((key: any) => {
            const temp: any = { lineItems: key };

            periods.forEach((period: any) => {
                const periodKey = `items${period}`;
                temp[periodKey] = dataByPeriod[period]?.[key];
            });

            return temp;
        });
    }, [dataByPeriod, displayRows, keys, periods]);

    // Selection is an imperative grid ref read only when the export action runs.
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    const buildExportRows = useCallback(() => {
        const range = selection?.current?.range;
        const rangeRows = range ? Array.from({ length: range.height }, (_, index) => range.y + index) : [];
        const rangeColumns = range ? Array.from({ length: range.width }, (_, index) => range.x + index) : [];
        const selectedRowIndexes = [...new Set([...(selection?.rows.toArray() ?? []), ...rangeRows])];
        const selectedColumnIndexes = [...new Set([...(selection?.columns.toArray() ?? []), ...rangeColumns])];
        const selectedColumnSet = new Set(selectedColumnIndexes);
        const exportableColumns = columns.filter(
            (column: any, index) =>
                column.id !== 'change' &&
                !lockedPeriodColumnIds.has(column.id) &&
                (!selectedColumnIndexes.length || index === 0 || selectedColumnSet.has(index)),
        );
        const headers = exportableColumns.map((column: any) => column.title || column.id);
        const selectedRows = selectedRowIndexes.length
            ? selectedRowIndexes.map((index) => dummyData[index]).filter(Boolean)
            : dummyData;
        const exportRows = selectedRows.map((row: any) =>
            exportableColumns.map((column: any) =>
                column.id === 'lineItems'
                    ? row.displayLabel
                        ? String(row.displayLabel)
                        : formatFinancialLineItemLabel(row.lineItems)
                    : row[column.id],
            ),
        );

        return [headers, ...exportRows];
    }, [columns, dummyData, lockedPeriodColumnIds, selection]);

    const handleExport = useCallback(
        async (format: 'csv' | 'xlsx') => {
            if (exporting) {
                return;
            }

            setExporting(true);
            setExportingFormat(format);

            const label = format === 'csv' ? 'CSV' : 'Excel';
            const toastId = toast.loading(`Downloading ${label}`, {
                description: 'Preparing your financial data export.',
            });

            try {
                const symbol = companySymbol ? `${companySymbol}-` : '';
                const exportTimestamp = new Date().toISOString().replace(/[:.]/gu, '-');
                const exportedFileName = `${symbol}Financials-${segmentedValue}-${exportTimestamp}`;

                await downloadSpreadsheet(buildExportRows(), exportedFileName, format, 'Financials');

                toast.success(`${label} Download Started`, {
                    id: toastId,
                    description: 'Your export should appear in your downloads shortly.',
                });
            } catch (error) {
                captureException(error);
                toast.error(`Error Downloading ${label}`, {
                    id: toastId,
                });
            } finally {
                setExporting(false);
                setExportingFormat(null);
            }
        },
        [buildExportRows, companySymbol, exporting, segmentedValue],
    );

    const canSyncGoogleSheets = hasMinimumPlan('pro');

    const handleSyncGoogleSheets = useCallback(async () => {
        if (!canSyncGoogleSheets) {
            return;
        }

        const googleSheetsAccounts = connectedAccounts?.filter(
            (connectedAccount: ConnectedAccount) => connectedAccount?.type === ConnectedAccountType.GOOGLE_SHEETS,
        );

        if (!googleSheetsAccounts?.length) {
            toast('Connect Google Sheets', {
                description: 'Add Google Sheets as a data source before syncing financial data.',
            });
            setDropdownOpen(false);
            setDataSourcesModalOpen(true);
            return;
        }

        if (!companySymbol) {
            toast('Sync Unavailable', {
                description: 'This table can be exported now. Google Sheets sync requires a company symbol.',
            });
            return;
        }

        setExporting(true);

        try {
            await authorizedAxiosInstance.put('/api/connected-accounts/sync', {
                connectedAccountsIds: googleSheetsAccounts.map(
                    (connectedAccount: ConnectedAccount) => connectedAccount.id,
                ),
                tickerSymbols: [companySymbol],
                reports: ['Balance Sheet', 'Income Statement', 'Statement of Cash Flows'],
                delineation: segmentedValue,
                accountingFormat: 'asReported',
            });

            toast('Sync Created', {
                description: `${companySymbol} financial data is syncing to Google Sheets.`,
            });
        } catch (error) {
            captureException(error);
            toast('Error Syncing');
        } finally {
            setExporting(false);
        }
    }, [canSyncGoogleSheets, companySymbol, connectedAccounts, segmentedValue]);

    const exportDropdown = (
        <div
            style={{
                minWidth: '16rem',
                border: `1px solid ${token.colorBorder}`,
                backgroundColor: token.colorBgElevated,
                borderRadius: token.borderRadius,
                boxShadow: token.boxShadowSecondary,
                padding: '0.25rem',
            }}
        >
            <Button
                block
                type='text'
                onClick={() => void handleExport('csv')}
                loading={exportingFormat === 'csv'}
                disabled={exporting && exportingFormat !== 'csv'}
            >
                <Row align='middle' wrap={false} gutter={[8, 0]} style={{ width: '100%' }}>
                    <Col flex='none'>
                        <TbFileTypeCsv size='1rem' style={{ verticalAlign: '-0.175rem' }} />
                    </Col>
                    <Col flex='none'>Export to CSV</Col>
                    <Col flex='auto' />
                    <Col flex='none'>
                        <small style={{ verticalAlign: '1px', color: token.colorTextTertiary }}>.csv</small>
                    </Col>
                </Row>
            </Button>
            <Button
                block
                type='text'
                onClick={() => void handleExport('xlsx')}
                loading={exportingFormat === 'xlsx'}
                disabled={exporting && exportingFormat !== 'xlsx'}
            >
                <Row align='middle' wrap={false} gutter={[8, 0]} style={{ width: '100%' }}>
                    <Col flex='none'>
                        <TbFileSpreadsheet size='1rem' style={{ verticalAlign: '-0.175rem' }} />
                    </Col>
                    <Col flex='none'>Export to Excel</Col>
                    <Col flex='auto' />
                    <Col flex='none'>
                        <small style={{ verticalAlign: '1px', color: token.colorTextTertiary }}>.xlsx</small>
                    </Col>
                </Row>
            </Button>
            <Divider style={{ margin: '0.25rem 0' }} />
            <small
                style={{
                    display: 'block',
                    padding: '0.25rem 0.5rem 0.375rem',
                    color: token.colorTextTertiary,
                    fontWeight: 500,
                }}
            >
                Data Sources
            </small>
            <Button
                block
                type='text'
                style={{
                    height: 'auto',
                    paddingTop: '0.375rem',
                    paddingBottom: '0.375rem',
                    textAlign: 'left',
                }}
                disabled={!canSyncGoogleSheets || exporting}
                onClick={handleSyncGoogleSheets}
                loading={exporting && !exportingFormat}
            >
                <Row align='middle' wrap={false} gutter={[8, 0]} style={{ width: '100%' }}>
                    <Col flex='none'>
                        <Avatar
                            size={16}
                            shape='square'
                            style={{ border: `1px solid ${token.colorBorder}` }}
                            icon={
                                <img
                                    alt='Google Sheets'
                                    src={GoogleSheetsSVGIcon}
                                    style={{ ...imgLogoStyling, transform: 'scale(0.75)' }}
                                />
                            }
                        />
                    </Col>
                    <Col flex='none'>Sync to Google Sheets</Col>
                    <Col flex='auto' />
                    <Col flex='none'>
                        <PlanTag plan='pro' style={{ marginRight: 0 }} />
                    </Col>
                </Row>
            </Button>
        </div>
    );

    return (
        <>
            <Dropdown
                open={dropdownOpen}
                trigger={['click']}
                popupRender={() => exportDropdown}
                placement='bottomLeft'
                overlayStyle={{ zIndex: 1050 }}
                onOpenChange={setDropdownOpen}
            >
                <span onClick={(event) => event.stopPropagation()} onMouseDown={(event) => event.stopPropagation()}>
                    <SmallTooltip title='Export Table' placement='topLeft'>
                        <Button
                            aria-label='Export table'
                            loading={exporting}
                            size='small'
                            style={{ marginRight: '0.25rem', ...buttonStyle }}
                            type='text'
                            icon={
                                <TbTableExport style={{ color: token.colorTextTertiary, verticalAlign: '-0.1rem' }} />
                            }
                        />
                    </SmallTooltip>
                </span>
            </Dropdown>
            <AddDataSourceModal
                activeView='manage'
                open={dataSourcesModalOpen}
                setOpen={setDataSourcesModalOpen}
                zIndex={1100}
            />
        </>
    );
};

export default GlideDataGridTableExportDropdown;
